import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp, Users, Award } from 'lucide-react';

const About = () => {
  const { t } = useLanguage();
  const publicUrl = process.env.PUBLIC_URL || '';
  const aboutBrandSrc = publicUrl.endsWith('/') ? `${publicUrl}wsj-about-brand.png` : `${publicUrl}/wsj-about-brand.png`;
  const aboutSecondary = String(t.about.description2 || '');
  const aboutSecondaryBlocks = aboutSecondary.includes('\n\n')
    ? aboutSecondary.split('\n\n').map((s) => s.trim()).filter(Boolean)
    : [aboutSecondary.trim()].filter(Boolean);

  const stats = [
    {
      icon: TrendingUp,
      value: '10+',
      label: t.about.yearsTitle
    },
    {
      icon: Users,
      value: '100+',
      label: t.about.clientsTitle
    },
    {
      icon: Award,
      value: '200+',
      label: t.about.projectsTitle
    }
  ];

  return (
    <section id="about" className="py-32 relative overflow-hidden w-full min-w-0">
      {/* Background Elements */}
      <div className="absolute inset-0 tech-grid opacity-20"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              {t.about.title}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-yellow-500 to-yellow-600 mx-auto rounded-full"></div>
          </div>

          {/* Content */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            {/* Brand visual */}
            <div className="relative group flex justify-center lg:justify-start order-2 lg:order-1">
              <div
                className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-blue-500/20 via-transparent to-amber-500/10 blur-2xl opacity-90 transition-opacity duration-500 group-hover:opacity-100"
                aria-hidden
              />
              <div className="relative w-full max-w-lg mx-auto lg:mx-0 rounded-2xl border border-white/10 bg-slate-950/50 backdrop-blur-sm px-4 py-8 sm:px-10 sm:py-12 flex items-center justify-center min-h-[220px] max-h-[min(340px,48vh)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_20px_50px_-24px_rgba(0,0,0,0.65)]">
                <img
                  src={aboutBrandSrc}
                  alt={t.about.title}
                  className="w-full max-h-[min(280px,42vh)] object-contain object-center drop-shadow-[0_10px_36px_rgba(0,0,0,0.55)]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Text */}
            <div className="space-y-5 order-1 lg:order-2">
              <p className="text-base sm:text-lg text-white/70 leading-relaxed font-light tracking-wide break-words">
                {t.about.description}
              </p>
              {aboutSecondaryBlocks.map((block, i) => (
                <p
                  key={i}
                  className="text-base sm:text-lg text-white/70 leading-relaxed font-light tracking-wide break-words"
                >
                  {block}
                </p>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="card-premium p-6 sm:p-8 rounded-2xl group text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-5xl font-black text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-white/40 uppercase tracking-wider font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
